import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

export default function CharlesLawCalculator() {
  const [v1, setV1] = useState("");
  const [t1, setT1] = useState("");
  const [v2, setV2] = useState("");
  const [t2, setT2] = useState("");

  const calculateMissing = () => {
    const values = [v1, t1, v2, t2];
    const emptyCount = values.filter(v => !v).length;

    if (emptyCount !== 1) {
      toast({
        title: "Invalid Input",
        description: "Please provide exactly 3 values to calculate the 4th.",
        variant: "destructive",
      });
      return;
    }

    const V1 = parseFloat(v1);
    const T1 = parseFloat(t1);
    const V2 = parseFloat(v2);
    const T2 = parseFloat(t2);

    // V1/T1 = V2/T2
    if (!v1 && T1 && V2 && T2) {
      const result = (V2 * T1) / T2;
      setV1(result.toFixed(4));
      toast({ title: "V₁ Calculated", description: `V₁ = ${result.toFixed(4)} L` });
    } else if (!t1 && V1 && V2 && T2) {
      const result = (V2 * T2) / V1;
      setT1(result.toFixed(4));
      toast({ title: "T₁ Calculated", description: `T₁ = ${result.toFixed(4)} K` });
    } else if (!v2 && V1 && T1 && T2) {
      const result = (V1 * T2) / T1;
      setV2(result.toFixed(4));
      toast({ title: "V₂ Calculated", description: `V₂ = ${result.toFixed(4)} L` });
    } else if (!t2 && V1 && T1 && V2) {
      const result = (V2 * T1) / V1;
      setT2(result.toFixed(4));
      toast({ title: "T₂ Calculated", description: `T₂ = ${result.toFixed(4)} K` });
    }
  };

  const reset = () => {
    setV1("");
    setT1("");
    setV2("");
    setT2("");
  };

  return (
    <PageLayout 
      calculatorName="Charles's Law Calculator" 
      calculatorPath="/calculator/charles-law"
      category="chemistry"
      subcategory="Gases"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Charles's Law Calculator</CardTitle>
            <CardDescription>
              V₁/T₁ = V₂/T₂ (Pressure constant)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="v1">Initial Volume V₁ (L)</Label>
                  <Input
                    id="v1"
                    type="number"
                    step="any"
                    value={v1}
                    onChange={(e) => setV1(e.target.value)}
                    placeholder="Leave blank to solve"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="t1">Initial Temperature T₁ (K)</Label>
                  <Input
                    id="t1"
                    type="number"
                    step="any"
                    value={t1}
                    onChange={(e) => setT1(e.target.value)}
                    placeholder="Leave blank to solve"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="v2">Final Volume V₂ (L)</Label>
                  <Input
                    id="v2"
                    type="number"
                    step="any"
                    value={v2}
                    onChange={(e) => setV2(e.target.value)}
                    placeholder="Leave blank to solve"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="t2">Final Temperature T₂ (K)</Label>
                  <Input
                    id="t2"
                    type="number"
                    step="any"
                    value={t2}
                    onChange={(e) => setT2(e.target.value)}
                    placeholder="Leave blank to solve"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculateMissing} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {(v1 || t1 || v2 || t2) && (
              <CalculatorSuperCalcCTA config={calculatorCTAs["charles-law"]} trigger={v1 + t1 + v2 + t2} />
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Charles's Law</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono">V₁/T₁ = V₂/T₂</p>
                <p className="text-sm text-muted-foreground mt-2">
                  At constant pressure, volume and temperature are directly proportional. 
                  Temperature must be in Kelvin.
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
